import { CountryModel, RegionModel } from "@/apidtos";
import { LabeledInput } from "@core/components/forms/LabeledInput";
import { LabeledSelect } from "@core/components/forms/LabeledSelect";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { LabeledTextarea } from "@core/components/forms/LabeledTextarea";
import { useSiteConfig } from "@core/hooks/useSiteConfig";
import { useAddressBookConfig } from "@core/hooks/useAddessBookConfig";

type AddressFormInputsProps = {
  prefix?: string;
  regions: RegionModel[];
  countries: CountryModel[];
  disabled?: boolean;
};

export const AddressFormInputs = ({ prefix = "", regions, countries, disabled }: AddressFormInputsProps) => {
  const { register, control, setValue, getValues, formState: { errors } } = useFormContext();
  const addressBookConfig = useAddressBookConfig();
  const [showStreet2, setShowStreet2] = useState(Boolean(getValues(`${prefix}Street2`)) || addressBookConfig.ShowStreet2ByDefault);
  const [showStreet3, setShowStreet3] = useState(Boolean(getValues(`${prefix}Street3`)));
  const { t } = useTranslation();
  const siteConfig = useSiteConfig();

  // useWatch instead of watch, to localize the re-render to just this component instead of the entire form's children
  const countryId: number = useWatch({
    name: `${prefix}CountryId`,
    defaultValue: countries[0].Id,
    control
  });
  const availableRegions = regions.filter(x => x.CountryId === countryId) ?? [];

  const handleCountrySelection = (e: any) => {
    const regionId: number = getValues(`${prefix}RegionId`);
    const selectedRegion = regions.find(x => x.Id === regionId);
    if (selectedRegion?.CountryId !== countryId) {
      setValue(`${prefix}RegionId`, null);
    }
    setValue(`${prefix}CountryId`, e.target.value)
  }

  const handleAddStreet = () => {
    if (!showStreet2) {
      setShowStreet2(true);
    } else if (!showStreet3) {
      setShowStreet3(true);
    }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <LabeledInput disabled={disabled}
        required
        label={t("ui.nickname", "Nickname")}
        placeholder={t("ui.nickname", "Nickname")}
        className="font-medium"
        inputClassName="placeholder:text-black"
        {...register(`${prefix}DisplayName`, {
          required: "Please provide a nickname for this address."
        })}
        errors={errors} />
      <div>
        <LabeledInput disabled={disabled}
          required
          label={t("ui.street", "Street")}
          placeholder={t("ui.street", "Street")}
          className="font-medium"
          inputClassName="placeholder:text-black"
          {...register(`${prefix}Street1`, {
            required: "Please provide a street address."
          })}
          errors={errors} />
        {showStreet2 && <LabeledInput disabled={disabled} label={t("ui.street2", "Street 2")} {...register(`${prefix}Street2`)} />}
        {showStreet3 && <LabeledInput disabled={disabled} label={t("ui.street3", "Street 3")} {...register(`${prefix}Street3`)} />}
        {!showStreet3 && !addressBookConfig.ShowStreet2ByDefault && (
          <button
            type="button"
            onClick={handleAddStreet}
            className="mt-1 block bg-transparent border-0 p-0 text-sm underline cursor-pointer"
            style={{ color: "inherit", fontFamily: "inherit", textUnderlineOffset: 2 }}
          >
            {t("ui.addAnotherStreet", "Add another street")}
          </button>
        )}
      </div>
      <LabeledInput disabled={disabled}
        required
        label={t("ui.city", "City")}
        placeholder={t("ui.city", "City")}
        className="font-medium"
        inputClassName="placeholder:text-black"
        {...register(`${prefix}City`, {
          required: "Please provide a city."
        })}
        errors={errors} />
      <LabeledSelect
        disabled={!availableRegions.length || disabled}
        required={availableRegions.length > 0}
        label={t("ui.state", "State")}
        {...register(`${prefix}RegionId`, {
          valueAsNumber: true,
          validate: value => {
            if (availableRegions.length > 0 && !value) {
              return "Please select a state.";
            }
          }
        })}
        errors={errors}>
        <option value="">{t("ui.select", "Select")}</option>
        {availableRegions.map(r => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
      </LabeledSelect>
      <LabeledInput disabled={disabled}
        required
        label={t("ui.postalCode", "Postal Code")}
        placeholder={t("ui.postalCode", "Postal Code")}
        className="font-medium"
        inputClassName="placeholder:text-black"
        {...register(`${prefix}PostalCode`, {
          required: "Please provide a postal code."
        })}
        errors={errors} />
      <LabeledSelect disabled={disabled}
        required={countries.length > 0}
        label={t("ui.country", "Country")}
        {...register(`${prefix}CountryId`, {
          valueAsNumber: true,
          validate: value => {
            if (countries.length > 0 && !value) {
              return "Please select a country.";
            }
          }
        })}
        onChange={handleCountrySelection}
        errors={errors}>
        {countries.map(c => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
      </LabeledSelect>
      <LabeledInput disabled={disabled}
        label={t("ui.email", "Email")}
        placeholder={t("ui.email", "Email")}
        className="font-medium"
        inputClassName="placeholder:text-black"
        {...register(`${prefix}Email`, {
          validate: (value) => {
            if (!value) {
              return true;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return (
              emailRegex.test(value) ||
              "Must be a valid email address"
            );
          },
        })}
        errors={errors} />
      <LabeledInput disabled={disabled}
        label={t("ui.phoneNumber", "Phone Number")}
        placeholder={t("ui.phoneNumber", "Phone Number")}
        className="font-medium"
        inputClassName="placeholder:text-black"
        required
        {...register(`${prefix}PhoneNumber`, {
          required: t("ui.pleaseEnterAPhoneNumber", "Please provide a phone number"),
        })}
        errors={errors} />
      <ExtensionPoint name="AddressFormInputs" prefix={prefix} disabled={disabled} />
      {siteConfig?.ShowAddressInstructionsField && (
        <LabeledTextarea
          label={t("ui.Instructions", "Instructions (gate code, special delivery)")}
          placeholder={t("ui.gateCodeExample", "The gate code is 1234")}
          className="font-medium col-span-2 mb-4"
          {...register(`${prefix}Instructions`)}
        />
      )}
    </div>
  );
};
